import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  LIST_REQUEST, listJobsSuccess, listJobsFailure,
  CREATE_REQUEST, createJobSuccess, createJobFailure,
  UPDATE_REQUEST, updateJobSuccess, updateJobFailure,
  DELETE_REQUEST, deleteJobSuccess, deleteJobFailure,
  TRIGGER_REQUEST, triggerJobSuccess, triggerJobFailure,
  PREVIEW_SCHEDULE_REQUEST, previewScheduleSuccess, previewScheduleFailure
} from '../../actions/ingestion/jobs';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error as ErrorAction } from '../../actions/error/error';
import { 
  processApiResponse, 
  handleSagaError, 
  createSuccessPayload, 
  createErrorPayload, extractErrorMessage } from '../../../utils/apiResponseHandler';

// Helper function to get headers with workspace ID
function getHeaders() {
  const userHeaders = getLocalUser();
  // getLocalUser() already includes X-WORKSPACE-ID from localStorage
  return userHeaders;
}

// List jobs saga
function* listJobsSaga(action) {
  try {
    const headers = getHeaders();
    const { sourceId } = action.payload || {};
    
    let url = `${config.API_URL}/ingestion/jobs`;
    if (sourceId) {
      url += `?source_id=${sourceId}`;
    }
    
    const response = yield call(
      axios.get,
      url,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'list jobs');
    
    if (result.success) {
      yield put(listJobsSuccess(result.data));
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'list jobs');
    yield put(ErrorAction(error));
    yield put(listJobsFailure(errorPayload));
  }
}

// Create job saga
function* createJobSaga(action) {
  try {
    const headers = getHeaders();
    
    const response = yield call(
      axios.post,
      `${config.API_URL}/ingestion/jobs`,
      action.payload,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'create import job');
    
    if (result.success) {
      yield put(createJobSuccess(createSuccessPayload(result.data)));
      yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: result.message });
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'create import job');
    yield put(ErrorAction(error));
    yield put(createJobFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Update job saga
function* updateJobSaga(action) {
  try {
    const headers = getHeaders();
    const { jobId, ...updateData } = action.payload;
    
    const response = yield call(
      axios.put,
      `${config.API_URL}/ingestion/jobs/${jobId}`,
      updateData,
      { headers }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'update job');
    
    if (result.success) {
      yield put(updateJobSuccess(createSuccessPayload({ ...result.data, job_id: jobId })));
      yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: result.message });
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'update job');
    yield put(ErrorAction(error));
    yield put(updateJobFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Delete job saga
function* deleteJobSaga(action) {
  try {
    const headers = getHeaders();
    const { jobId } = action.payload;
    
    yield call(
      axios.delete,
      `${config.API_URL}/ingestion/jobs/${jobId}`,
      { headers }
    );
    
    yield put(deleteJobSuccess({ jobId }));
    yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: 'Import job deleted successfully' });
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(deleteJobFailure(extractErrorMessage(error)));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: 'Failed to delete import job' });
  }
}

// Trigger job saga
function* triggerJobSaga(action) {
  try {
    const headers = getHeaders();
    const { jobId } = action.payload;
    
    const response = yield call(
      axios.post,
      `${config.API_URL}/ingestion/jobs/${jobId}/trigger`,
      {},
      { headers }
    );
    
    yield put(triggerJobSuccess({ jobId, ...response.data }));
    yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: 'Job triggered successfully' });
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(triggerJobFailure(extractErrorMessage(error)));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: 'Failed to trigger job' });
  }
}

// Preview schedule saga
function* previewScheduleSaga(action) {
  try {
    const headers = getHeaders();
    const { jobId, n } = action.payload;
    
    const response = yield call(
      axios.get,
      `${config.API_URL}/ingestion/jobs/${jobId}/preview-schedule?n=${n}`,
      { headers }
    );
    
    yield put(previewScheduleSuccess({ jobId, ...response.data }));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(previewScheduleFailure(extractErrorMessage(error)));
  }
}

// Watcher saga
export function* watch() {
  yield takeLatest(LIST_REQUEST, listJobsSaga);
  yield takeLatest(CREATE_REQUEST, createJobSaga);
  yield takeLatest(UPDATE_REQUEST, updateJobSaga);
  yield takeLatest(DELETE_REQUEST, deleteJobSaga);
  yield takeLatest(TRIGGER_REQUEST, triggerJobSaga);
  yield takeLatest(PREVIEW_SCHEDULE_REQUEST, previewScheduleSaga);
}
